import {useEffect, useState} from 'react'
import {useI18n} from '@/i18n'
import {Button} from '@/components/ui/button'
import {ButtonSpinner} from '@/components/shared/ButtonSpinner'
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import {DialogBody} from '@/components/shared/DialogBody'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {Textarea} from '@/components/ui/textarea'
import {Switch} from '@/components/ui/switch'
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select'
import {SecretReferenceInput} from '@/components/shared/SecretReferenceInput'
import {FormField} from '@/components/shared/FormField'
import {FormGrid} from '@/components/shared/FormGrid'
import {FormSection} from '@/components/shared/FormSection'
import {InfoBanner} from '@/components/shared/InfoBanner'
import type {SapAbapOutboundConfig} from '@/types/outbound-connector'
import {useUpdateSapAbapConfig} from '@/hooks/use-sap-configs'

interface SapAbapOutboundConfigEditDialogProps {
    connectorId: string
    config: SapAbapOutboundConfig
    open: boolean
    onClose: () => void
}

const AUTH_TYPE_OPTIONS = ['OAUTH2_CLIENT', 'BASIC_AUTH', 'CERTIFICATE'] as const
const USER_TYPE_OPTIONS = ['A', 'B', 'C', 'S'] as const
const ROLE_ASSIGNMENT_OPTIONS = ['RFC', 'ODATA'] as const
const COMPOSITE_OPTIONS = ['NATIVE', 'EXPLICIT'] as const
const PASSWORD_POLICY_OPTIONS = ['GENERATED', 'INVITE'] as const

interface ActionToggleProps {
    id: string
    label: string
    checked: boolean
    onChange: (v: boolean) => void
}

function ActionToggle({id, label, checked, onChange}: ActionToggleProps) {
    return (
        <div className="flex items-center justify-between gap-2">
            <Label htmlFor={id}>{label}</Label>
            <Switch id={id} checked={checked} onCheckedChange={onChange}/>
        </div>
    )
}

export function SapAbapOutboundConfigEditDialog({
    connectorId, config, open, onClose,
}: SapAbapOutboundConfigEditDialogProps) {
    const i18n = useI18n()
    const updateMutation = useUpdateSapAbapConfig(connectorId)

    const [odataBaseUrl, setOdataBaseUrl] = useState(config.odataBaseUrl)
    const [rfcBaseUrl, setRfcBaseUrl] = useState(config.rfcBaseUrl ?? '')
    const [authType, setAuthType] = useState(config.authType)
    const [tokenUrl, setTokenUrl] = useState(config.tokenUrl ?? '')
    const [clientId, setClientId] = useState(config.clientId ?? '')
    const [clientSecretRef, setClientSecretRef] = useState('')
    const [scope, setScope] = useState(config.scope ?? '')
    const [username, setUsername] = useState(config.username ?? '')
    const [passwordSecretRef, setPasswordSecretRef] = useState('')
    const [clientCertSecretRef, setClientCertSecretRef] = useState('')
    const [clientKeySecretRef, setClientKeySecretRef] = useState('')
    const [truststoreSecretRef, setTruststoreSecretRef] = useState('')
    const [mandant, setMandant] = useState(config.mandant)
    const [userTypeDefault, setUserTypeDefault] = useState(config.userTypeDefault)
    const [roleAssignmentMode, setRoleAssignmentMode] = useState(config.roleAssignmentMode)
    const [compositeRoleExpansion, setCompositeRoleExpansion] = useState(config.compositeRoleExpansion)
    const [bnameTemplate, setBnameTemplate] = useState(config.bnameTemplate)
    const [passwordPolicy, setPasswordPolicy] = useState(config.passwordPolicy)
    const [lockReason, setLockReason] = useState(config.lockReason)
    const [etagRetryLimit, setEtagRetryLimit] = useState(String(config.etagRetryLimit))
    const [bapiAllowlist, setBapiAllowlist] = useState(config.bapiAllowlist ?? '')
    const [createEnabled, setCreateEnabled] = useState(config.createEnabled)
    const [updateEnabled, setUpdateEnabled] = useState(config.updateEnabled)
    const [deleteEnabled, setDeleteEnabled] = useState(config.deleteEnabled)
    const [enableEnabled, setEnableEnabled] = useState(config.enableEnabled)
    const [disableEnabled, setDisableEnabled] = useState(config.disableEnabled)
    const [grantEnabled, setGrantEnabled] = useState(config.grantEnabled)
    const [revokeEnabled, setRevokeEnabled] = useState(config.revokeEnabled)
    const [connectTimeoutMs, setConnectTimeoutMs] = useState(String(config.connectTimeoutMs))
    const [readTimeoutMs, setReadTimeoutMs] = useState(String(config.readTimeoutMs))
    const [maxRetries, setMaxRetries] = useState(String(config.maxRetries))

    useEffect(() => {
        if (open) {
            setOdataBaseUrl(config.odataBaseUrl)
            setRfcBaseUrl(config.rfcBaseUrl ?? '')
            setAuthType(config.authType)
            setTokenUrl(config.tokenUrl ?? '')
            setClientId(config.clientId ?? '')
            setClientSecretRef('')
            setScope(config.scope ?? '')
            setUsername(config.username ?? '')
            setPasswordSecretRef('')
            setClientCertSecretRef('')
            setClientKeySecretRef('')
            setTruststoreSecretRef('')
            setMandant(config.mandant)
            setUserTypeDefault(config.userTypeDefault)
            setRoleAssignmentMode(config.roleAssignmentMode)
            setCompositeRoleExpansion(config.compositeRoleExpansion)
            setBnameTemplate(config.bnameTemplate)
            setPasswordPolicy(config.passwordPolicy)
            setLockReason(config.lockReason)
            setEtagRetryLimit(String(config.etagRetryLimit))
            setBapiAllowlist(config.bapiAllowlist ?? '')
            setCreateEnabled(config.createEnabled)
            setUpdateEnabled(config.updateEnabled)
            setDeleteEnabled(config.deleteEnabled)
            setEnableEnabled(config.enableEnabled)
            setDisableEnabled(config.disableEnabled)
            setGrantEnabled(config.grantEnabled)
            setRevokeEnabled(config.revokeEnabled)
            setConnectTimeoutMs(String(config.connectTimeoutMs))
            setReadTimeoutMs(String(config.readTimeoutMs))
            setMaxRetries(String(config.maxRetries))
        }
    }, [open, config])

    const createWithoutGrant = !createEnabled && grantEnabled
    const disableWithoutDelete = !disableEnabled && deleteEnabled

    const handleSave = () => {
        updateMutation.mutate({
            odataBaseUrl,
            rfcBaseUrl: rfcBaseUrl || null,
            authType,
            tokenUrl: tokenUrl || null,
            clientId: clientId || null,
            clientSecretRef: clientSecretRef || null,
            scope: scope || null,
            username: username || null,
            passwordSecretRef: passwordSecretRef || null,
            clientCertSecretRef: clientCertSecretRef || null,
            clientKeySecretRef: clientKeySecretRef || null,
            truststoreSecretRef: truststoreSecretRef || null,
            mandant,
            userTypeDefault,
            roleAssignmentMode,
            compositeRoleExpansion,
            bnameTemplate,
            passwordPolicy,
            lockReason,
            etagRetryLimit: parseInt(etagRetryLimit, 10) || 0,
            bapiAllowlist: bapiAllowlist || null,
            createEnabled,
            updateEnabled,
            deleteEnabled,
            enableEnabled,
            disableEnabled,
            grantEnabled,
            revokeEnabled,
            connectTimeoutMs: parseInt(connectTimeoutMs, 10) || 5000,
            readTimeoutMs: parseInt(readTimeoutMs, 10) || 30000,
            maxRetries: parseInt(maxRetries, 10) || 0,
            defaultRoles: null,
            version: config.version,
        }, {onSuccess: onClose})
    }

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-2xl overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{i18n.connectors.sapAbap.editDialogTitle}</DialogTitle>
                    <DialogDescription>{i18n.connectors.sapAbap.editDialogDescription}</DialogDescription>
                </DialogHeader>
                <DialogBody>
                    <FormSection title={i18n.connectors.sapAbap.sectionConnection}>
                        <FormGrid columns={1}>
                            <FormField label={i18n.connectors.sapAbap.odataBaseUrl} htmlFor="sap-abap-odataBaseUrl" required>
                                <Input id="sap-abap-odataBaseUrl" value={odataBaseUrl}
                                       onChange={e => setOdataBaseUrl(e.target.value)}/>
                            </FormField>
                            <FormField label={i18n.connectors.sapAbap.rfcBaseUrl} htmlFor="sap-abap-rfcBaseUrl">
                                <Input id="sap-abap-rfcBaseUrl" value={rfcBaseUrl}
                                       onChange={e => setRfcBaseUrl(e.target.value)}/>
                            </FormField>
                        </FormGrid>
                        <FormGrid columns={2}>
                            <FormField label={i18n.connectors.sapAbap.mandant} htmlFor="sap-abap-mandant" required>
                                <Input id="sap-abap-mandant" value={mandant}
                                       onChange={e => setMandant(e.target.value)} maxLength={3}/>
                            </FormField>
                            <FormField label={i18n.connectors.sapAbap.userTypeDefault} htmlFor="sap-abap-userType" required>
                                <Select value={userTypeDefault} onValueChange={setUserTypeDefault}>
                                    <SelectTrigger id="sap-abap-userType" data-testid="select-sap-abap-user-type">
                                        <SelectValue/>
                                    </SelectTrigger>
                                    <SelectContent>
                                        {USER_TYPE_OPTIONS.map(o => (
                                            <SelectItem key={o} value={o}>{o}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormField>
                        </FormGrid>
                    </FormSection>

                    <FormSection title={i18n.connectors.sapAbap.sectionAuthentication}>
                        <FormGrid columns={1}>
                            <FormField label={i18n.connectors.authType} htmlFor="sap-abap-authType" required>
                                <Select value={authType} onValueChange={setAuthType}>
                                    <SelectTrigger id="sap-abap-authType" data-testid="select-sap-abap-auth-type">
                                        <SelectValue/>
                                    </SelectTrigger>
                                    <SelectContent>
                                        {AUTH_TYPE_OPTIONS.map(o => (
                                            <SelectItem key={o} value={o}>{o}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormField>
                        </FormGrid>
                        {authType === 'OAUTH2_CLIENT' && (
                            <FormGrid columns={2}>
                                <FormField label={i18n.connectors.sapAbap.clientId} htmlFor="sap-abap-clientId">
                                    <Input id="sap-abap-clientId" value={clientId}
                                           onChange={e => setClientId(e.target.value)}/>
                                </FormField>
                                <FormField label={i18n.connectors.sapAbap.clientSecretRef} htmlFor="sap-abap-clientSecretRef">
                                    <SecretReferenceInput id="sap-abap-clientSecretRef"
                                                          value={clientSecretRef}
                                                          onChange={setClientSecretRef}/>
                                </FormField>
                                <FormField label={i18n.connectors.sapAbap.tokenUrl} htmlFor="sap-abap-tokenUrl">
                                    <Input id="sap-abap-tokenUrl" value={tokenUrl}
                                           onChange={e => setTokenUrl(e.target.value)}/>
                                </FormField>
                                <FormField label={i18n.connectors.sapAbap.scope} htmlFor="sap-abap-scope">
                                    <Input id="sap-abap-scope" value={scope}
                                           onChange={e => setScope(e.target.value)}/>
                                </FormField>
                            </FormGrid>
                        )}
                        {authType === 'BASIC_AUTH' && (
                            <FormGrid columns={2}>
                                <FormField label={i18n.connectors.sapAbap.username} htmlFor="sap-abap-username">
                                    <Input id="sap-abap-username" value={username}
                                           onChange={e => setUsername(e.target.value)}/>
                                </FormField>
                                <FormField label={i18n.connectors.sapAbap.passwordSecretRef} htmlFor="sap-abap-passwordSecretRef">
                                    <SecretReferenceInput id="sap-abap-passwordSecretRef"
                                                          value={passwordSecretRef}
                                                          onChange={setPasswordSecretRef}/>
                                </FormField>
                            </FormGrid>
                        )}
                        {authType === 'CERTIFICATE' && (
                            <FormGrid columns={2}>
                                <FormField label={i18n.connectors.sapAbap.clientCertSecretRef} htmlFor="sap-abap-clientCertSecretRef">
                                    <SecretReferenceInput id="sap-abap-clientCertSecretRef"
                                                          value={clientCertSecretRef}
                                                          onChange={setClientCertSecretRef}/>
                                </FormField>
                                <FormField label={i18n.connectors.sapAbap.clientKeySecretRef} htmlFor="sap-abap-clientKeySecretRef">
                                    <SecretReferenceInput id="sap-abap-clientKeySecretRef"
                                                          value={clientKeySecretRef}
                                                          onChange={setClientKeySecretRef}/>
                                </FormField>
                                <FormField label={i18n.connectors.sapAbap.truststoreSecretRef}
                                           htmlFor="sap-abap-truststoreSecretRef" fullWidth>
                                    <SecretReferenceInput id="sap-abap-truststoreSecretRef"
                                                          value={truststoreSecretRef}
                                                          onChange={setTruststoreSecretRef}/>
                                </FormField>
                            </FormGrid>
                        )}
                    </FormSection>

                    <FormSection title={i18n.connectors.sapAbap.sectionProvisioning}>
                        <FormGrid columns={2}>
                            <FormField label={i18n.connectors.sapAbap.roleAssignmentMode} htmlFor="sap-abap-roleAssignmentMode" required>
                                <Select value={roleAssignmentMode} onValueChange={setRoleAssignmentMode}>
                                    <SelectTrigger id="sap-abap-roleAssignmentMode" data-testid="sap-abap-role-assignment-mode-select">
                                        <SelectValue/>
                                    </SelectTrigger>
                                    <SelectContent>
                                        {ROLE_ASSIGNMENT_OPTIONS.map(o => (
                                            <SelectItem key={o} value={o}>{o}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormField>
                            <FormField label={i18n.connectors.sapAbap.compositeRoleExpansion} htmlFor="sap-abap-compositeRoleExpansion" required>
                                <Select value={compositeRoleExpansion} onValueChange={setCompositeRoleExpansion}>
                                    <SelectTrigger id="sap-abap-compositeRoleExpansion" data-testid="sap-abap-composite-role-expansion-select">
                                        <SelectValue/>
                                    </SelectTrigger>
                                    <SelectContent>
                                        {COMPOSITE_OPTIONS.map(o => (
                                            <SelectItem key={o} value={o}>{o}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormField>
                            <FormField label={i18n.connectors.sapAbap.bnameTemplate} htmlFor="sap-abap-bnameTemplate" required>
                                <Input id="sap-abap-bnameTemplate" value={bnameTemplate}
                                       onChange={e => setBnameTemplate(e.target.value)}/>
                            </FormField>
                            <FormField label={i18n.connectors.sapAbap.passwordPolicy} htmlFor="sap-abap-passwordPolicy" required>
                                <Select value={passwordPolicy} onValueChange={setPasswordPolicy}>
                                    <SelectTrigger id="sap-abap-passwordPolicy" data-testid="sap-abap-password-policy-select">
                                        <SelectValue/>
                                    </SelectTrigger>
                                    <SelectContent>
                                        {PASSWORD_POLICY_OPTIONS.map(o => (
                                            <SelectItem key={o} value={o}>{o}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormField>
                            <FormField label={i18n.connectors.sapAbap.lockReason} htmlFor="sap-abap-lockReason" required fullWidth>
                                <Input id="sap-abap-lockReason" value={lockReason}
                                       onChange={e => setLockReason(e.target.value)}/>
                            </FormField>
                            <FormField label={i18n.connectors.sapAbap.etagRetryLimit} htmlFor="sap-abap-etagRetryLimit">
                                <Input id="sap-abap-etagRetryLimit" type="number" value={etagRetryLimit}
                                       onChange={e => setEtagRetryLimit(e.target.value)}/>
                            </FormField>
                        </FormGrid>
                    </FormSection>

                    <FormSection title={i18n.connectors.sapAbap.allowedActions}>
                        <FormGrid columns={2}>
                            <ActionToggle id="sap-abap-create" label={i18n.connectors.sapAbap.actionCreate}
                                          checked={createEnabled} onChange={setCreateEnabled}/>
                            <ActionToggle id="sap-abap-update" label={i18n.connectors.sapAbap.actionUpdate}
                                          checked={updateEnabled} onChange={setUpdateEnabled}/>
                            <ActionToggle id="sap-abap-delete" label={i18n.connectors.sapAbap.actionDelete}
                                          checked={deleteEnabled} onChange={setDeleteEnabled}/>
                            <ActionToggle id="sap-abap-enable" label={i18n.connectors.sapAbap.actionEnable}
                                          checked={enableEnabled} onChange={setEnableEnabled}/>
                            <ActionToggle id="sap-abap-disable" label={i18n.connectors.sapAbap.actionDisable}
                                          checked={disableEnabled} onChange={setDisableEnabled}/>
                            <ActionToggle id="sap-abap-grant" label={i18n.connectors.sapAbap.actionGrant}
                                          checked={grantEnabled} onChange={setGrantEnabled}/>
                            <ActionToggle id="sap-abap-revoke" label={i18n.connectors.sapAbap.actionRevoke}
                                          checked={revokeEnabled} onChange={setRevokeEnabled}/>
                        </FormGrid>
                        {createWithoutGrant && (
                            <InfoBanner variant="warning">
                                {i18n.connectors.sapAbap.warnCreateWithoutGrant}
                            </InfoBanner>
                        )}
                        {disableWithoutDelete && (
                            <InfoBanner variant="warning">
                                {i18n.connectors.sapAbap.warnDisableWithoutDelete}
                            </InfoBanner>
                        )}
                    </FormSection>

                    <FormSection title={i18n.connectors.sapAbap.bapiAllowlist}>
                        <FormGrid columns={1}>
                            <FormField label={i18n.connectors.sapAbap.bapiAllowlist} htmlFor="sap-abap-bapiAllowlist">
                                <Textarea id="sap-abap-bapiAllowlist" value={bapiAllowlist}
                                          onChange={e => setBapiAllowlist(e.target.value)}
                                          rows={6}
                                          placeholder={i18n.connectors.sapAbap.bapiAllowlistPlaceholder}
                                          className="font-mono text-xs"/>
                            </FormField>
                        </FormGrid>
                    </FormSection>

                    <FormSection title={i18n.connectors.sapAbap.sectionTimeouts}>
                        <FormGrid columns={2}>
                            <FormField label={i18n.connectors.sapAbap.connectTimeoutMs} htmlFor="sap-abap-connectTimeoutMs">
                                <Input id="sap-abap-connectTimeoutMs" type="number" value={connectTimeoutMs}
                                       onChange={e => setConnectTimeoutMs(e.target.value)}/>
                            </FormField>
                            <FormField label={i18n.connectors.sapAbap.readTimeoutMs} htmlFor="sap-abap-readTimeoutMs">
                                <Input id="sap-abap-readTimeoutMs" type="number" value={readTimeoutMs}
                                       onChange={e => setReadTimeoutMs(e.target.value)}/>
                            </FormField>
                            <FormField label={i18n.connectors.sapAbap.maxRetries} htmlFor="sap-abap-maxRetries">
                                <Input id="sap-abap-maxRetries" type="number" value={maxRetries}
                                       onChange={e => setMaxRetries(e.target.value)}/>
                            </FormField>
                        </FormGrid>
                    </FormSection>
                </DialogBody>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} data-testid="btn-cancel-sap-abap-config">{i18n.common.cancel}</Button>
                    <Button onClick={handleSave} disabled={updateMutation.isPending}
                            data-testid="btn-save-sap-abap-config">
                        {updateMutation.isPending && <ButtonSpinner/>}
                        {i18n.common.save}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
